// src/modules/marketplace/components/realization/realizationTable.tsx

import { FC, useMemo } from "react"
import { Box } from "@mui/material"
import { GridColDef } from "@mui/x-data-grid-pro"
import parseISO from "date-fns/parseISO/index.js"
import { formatDate } from "@/core/utils"
import { GRID_PAGE_SIZE_OPTIONS } from "@/core/constants"
import { DataGrid } from "@core/components"
import { useAppDispatch, useAppSelector } from "@/modules/app/hooks"
import {
  getRealizationState,
  setRealizationPagination,
  useRealizationsListQuery,
} from "../../store"
import { Realization } from "../../interfaces"
import { RealizationSummary } from "./realizationSummary"

interface IProps {}

export const RealizationTable: FC<IProps> = () => {
  const dispatch = useAppDispatch()
  const {
    realizationPagination: { page, pageSize },
    realizationFilters,
  } = useAppSelector(getRealizationState)

  const { data, isFetching } = useRealizationsListQuery({
    queryParams: {
      page: page + 1,
      limit: pageSize,
      ...realizationFilters,
    },
  })

  const columns: GridColDef<Realization.RealizationRow>[] = useMemo(
    () => [
      {
        headerName: "Категория",
        field: "category",
        flex: 1.5,
        valueGetter: ({ row }) => row.category?.name ?? "—",
      },
      {
        headerName: "Фасовка",
        field: "package",
        flex: 1.5,
        valueGetter: ({ row }) =>
          row.package
            ? `${row.package.name} ${row.package.count} ${row.package.measure}`
            : "—",
      },
      {
        headerName: "Статус",
        field: "status",
        flex: 1,
      },
      {
        headerName: "Вес",
        field: "weight",
        flex: 0.8,
        valueGetter: ({ row }) => Number(row.weight),
        valueFormatter: ({ value }) => `${value} г`,
      },
      {
        headerName: "Себес",
        field: "fine",
        flex: 0.8,
        type: "number",
        valueFormatter: ({ value }) =>
          value != null ? Number(value).toLocaleString("ru-RU") : "—",
      },
      {
        headerName: "Оплата за клад",
        field: "courier_payment",
        flex: 0.8,
        type: "number",
        valueFormatter: ({ value }) =>
          value != null ? Number(value).toLocaleString("ru-RU") : "—",
      },
      {
        headerName: "Комиссия %",
        field: "commission_rate",
        flex: 0.7,
        valueFormatter: ({ value }) => (value ? `${value}%` : "—"),
      },
      {
        headerName: "Комиссия (руб)",
        field: "commission",
        flex: 0.8,
        valueGetter: ({ row }) => Number(row.commission),
        valueFormatter: ({ value }) =>
          value != null
            ? Number(value).toLocaleString("ru-RU", {
                minimumFractionDigits: 2,
              })
            : "—",
      },
      {
        headerName: "Цена (руб)",
        field: "price",
        flex: 0.8,
        type: "number",
        valueFormatter: ({ value }) =>
          value != null ? Number(value).toLocaleString("ru-RU") : "—",
      },
      {
        headerName: "Потрачено бонусов (руб)",
        field: "bonus_spent",
        flex: 0.8,
        type: "number",
        valueFormatter: ({ value }) =>
          value != null ? Number(value).toLocaleString("ru-RU") : "—",
      },
      {
        headerName: "Курьер",
        field: "courier",
        flex: 1,
        valueGetter: ({ row }) => row.courier?.name ?? "—",
      },
      {
        headerName: "Город",
        field: "city",
        flex: 1,
        valueGetter: ({ row }) => row.city?.name ?? "—",
      },
      {
        headerName: "Дата продажи",
        field: "date_sale",
        flex: 1,
        valueGetter: ({ row }) =>
          row.date_sale ? parseISO(row.date_sale) : null,
        valueFormatter: ({ value }) => (value ? formatDate(value) : "—"),
        type: "dateTime",
      },
      {
        headerName: "Дата загрузки",
        field: "loaded",
        flex: 1,
        valueGetter: ({ row }) => (row.loaded ? parseISO(row.loaded) : null),
        valueFormatter: ({ value }) => (value ? formatDate(value) : "—"),
        type: "dateTime",
      },
      {
        headerName: "Партия",
        field: "batch",
        flex: 1,
        valueGetter: ({ row }) => row.batch?.number ?? "—",
      },
    ],
    [],
  )

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <RealizationSummary summary={data?.summary} />
      <Box flex={1} minHeight={0}>
        <DataGrid<Realization.RealizationRow>
          columns={columns}
          rows={data?.data ?? []}
          getRowId={(row) => row.id}
          rowCount={data?.count ?? 0}
          loading={isFetching}
          filterMode="client"
          sortingMode="client"
          pagination
          paginationMode="server"
          pageSizeOptions={GRID_PAGE_SIZE_OPTIONS}
          onPaginationModelChange={(model) =>
            dispatch(setRealizationPagination(model))
          }
          initialState={{
            pagination: { paginationModel: { page, pageSize } },
          }}
        />
      </Box>
    </Box>
  )
}
